import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

function base(orgId) {
  return `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/partner`;
}

export async function listPartnerChannels(token, orgId) {
  const res = await axios.get(`${base(orgId)}/channels`, {
    headers: authHeaders(token),
  });
  return res.data?.channels || [];
}

export async function getPartnerChannel(token, orgId, channelId) {
  const res = await axios.get(`${base(orgId)}/channels/${channelId}`, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function markPartnerChannelRead(token, orgId, channelId) {
  const res = await axios.post(
    `${base(orgId)}/channels/${channelId}/read`,
    {},
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function getPartnerUnreadCount(token, orgId) {
  const res = await axios.get(`${base(orgId)}/unread-count`, {
    headers: authHeaders(token),
  });
  return Number(res.data?.unread_count) || 0;
}

export async function updatePartnerChannel(token, orgId, channelId, body) {
  const res = await axios.patch(
    `${base(orgId)}/channels/${channelId}`,
    body,
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function listPartnerMessages(token, orgId, channelId, opts = {}) {
  const res = await axios.get(
    `${base(orgId)}/channels/${channelId}/messages`,
    {
      headers: authHeaders(token),
      params: {
        limit: opts.limit,
        before_id: opts.beforeId,
      },
    }
  );
  return res.data?.messages || [];
}

export async function sendPartnerMessage(token, orgId, channelId, body) {
  const res = await axios.post(
    `${base(orgId)}/channels/${channelId}/messages`,
    body,
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function updatePartnerMessage(
  token,
  orgId,
  channelId,
  messageId,
  content
) {
  const res = await axios.patch(
    `${base(orgId)}/channels/${channelId}/messages/${messageId}`,
    { content },
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function deletePartnerMessage(
  token,
  orgId,
  channelId,
  messageId
) {
  const res = await axios.delete(
    `${base(orgId)}/channels/${channelId}/messages/${messageId}`,
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function togglePartnerReaction(
  token,
  orgId,
  channelId,
  messageId,
  emoji
) {
  const res = await axios.post(
    `${base(orgId)}/channels/${channelId}/messages/${messageId}/reactions`,
    { emoji },
    { headers: authHeaders(token) }
  );
  return res.data;
}

/** Send a partner-channel message with local file attachments (up to 10). */
export async function sendPartnerMedia(
  token,
  orgId,
  channelId,
  { files = [], content, reply_to_message_id } = {}
) {
  const form = new FormData();
  for (const file of files || []) {
    if (file) form.append("files", file);
  }
  if (content) form.append("content", content);
  if (reply_to_message_id != null) {
    form.append("reply_to_message_id", String(reply_to_message_id));
  }
  const res = await axios.post(
    `${base(orgId)}/channels/${channelId}/messages/media`,
    form,
    { headers: authHeaders(token) }
  );
  return res.data;
}

/** @deprecated use sendPartnerMedia */
export async function sendPartnerImage(token, orgId, channelId, file, content) {
  return sendPartnerMedia(token, orgId, channelId, {
    files: file ? [file] : [],
    content,
  });
}

export async function listPartnerships(token, orgId) {
  const res = await axios.get(`${base(orgId)}/partnerships`, {
    headers: authHeaders(token),
  });
  return res.data?.partnerships || [];
}

export async function searchPartnerDirectory(token, orgId, q, limit = 12) {
  const res = await axios.get(`${base(orgId)}/directory`, {
    headers: authHeaders(token),
    params: { q, limit },
  });
  return res.data?.organizations || [];
}

export async function invitePartner(token, orgId, body) {
  const res = await axios.post(`${base(orgId)}/partnerships/invite`, body, {
    headers: authHeaders(token),
  });
  return res.data;
}

export async function acceptPartnership(token, orgId, partnershipId) {
  const res = await axios.post(
    `${base(orgId)}/partnerships/${partnershipId}/accept`,
    {},
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function declinePartnership(token, orgId, partnershipId) {
  const res = await axios.post(
    `${base(orgId)}/partnerships/${partnershipId}/decline`,
    {},
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function endPartnership(token, orgId, partnershipId, reason) {
  const res = await axios.post(
    `${base(orgId)}/partnerships/${partnershipId}/end`,
    { reason },
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function addPartnerMember(token, orgId, channelId, userId, role) {
  const res = await axios.post(
    `${base(orgId)}/channels/${channelId}/members`,
    { user_id: userId, role },
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function setPartnerMemberRole(
  token,
  orgId,
  channelId,
  userId,
  role
) {
  const res = await axios.patch(
    `${base(orgId)}/channels/${channelId}/members/${encodeURIComponent(userId)}`,
    { role },
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function removePartnerMember(token, orgId, channelId, userId) {
  const res = await axios.delete(
    `${base(orgId)}/channels/${channelId}/members/${encodeURIComponent(userId)}`,
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function proposePartnerRetention(
  token,
  orgId,
  channelId,
  retentionDays
) {
  const res = await axios.post(
    `${base(orgId)}/channels/${channelId}/policy`,
    { retention_days: retentionDays },
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function resolvePartnerPolicy(
  token,
  orgId,
  channelId,
  proposalId,
  accept
) {
  const res = await axios.post(
    `${base(orgId)}/channels/${channelId}/policy/${proposalId}/resolve`,
    { accept },
    { headers: authHeaders(token) }
  );
  return res.data;
}

/** @deprecated Prefer resolvePartnerPolicy — resolves retention or hold-release. */
export async function resolvePartnerRetention(
  token,
  orgId,
  channelId,
  proposalId,
  accept
) {
  return resolvePartnerPolicy(token, orgId, channelId, proposalId, accept);
}

export async function setPartnerLegalHold(token, orgId, channelId, enabled) {
  const res = await axios.post(
    `${base(orgId)}/channels/${channelId}/legal-hold`,
    { enabled },
    { headers: authHeaders(token) }
  );
  return res.data;
}

export async function exportPartnerChannel(token, orgId, channelId) {
  const res = await axios.get(
    `${base(orgId)}/channels/${channelId}/export`,
    { headers: authHeaders(token), responseType: "blob" }
  );
  return res.data;
}
